import type { TruncatedDisplayResult } from './TruncatedList.types';

export const DATAVALUE_FALLBACK = '-';
export const SEPARATOR = ', ';
const MAX_LINES = 2;

function createProbe(containerWidth: number, font: string): HTMLSpanElement {
  const probe = document.createElement('span');
  probe.style.cssText = [
    'position:absolute',
    'visibility:hidden',
    'pointer-events:none',
    `width:${containerWidth}px`,
    `font:${font}`,
    'white-space:normal',
    'word-break:break-word',
    'line-height:inherit',
  ].join(';');
  document.body.appendChild(probe);
  return probe;
}

function findVisibleCount(
  items: string[],
  probe: HTMLSpanElement,
  twoLinesH: number
): number {
  let lo = 0;
  let hi = items.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    probe.textContent = items.slice(0, mid).join(SEPARATOR);
    if (probe.scrollHeight <= twoLinesH) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return lo;
}

export function buildTruncatedDisplay(
  items: string[],
  containerWidth: number,
  font: string,
  limit: number = items.length
): TruncatedDisplayResult {
  if (items.length === 0) return { visibleCount: 0, displayText: null };

  if (!containerWidth) {
    const count = Math.min(items.length, limit);
    return {
      visibleCount: count,
      displayText: items.slice(0, count).join(SEPARATOR) || null,
    };
  }

  const probe = createProbe(containerWidth, font);
  try {
    probe.textContent = 'X';
    const twoLinesH = probe.offsetHeight * MAX_LINES;

    const visibleCount = Math.min(
      limit,
      Math.max(
        1,
        findVisibleCount(items.slice(0, limit), probe, twoLinesH)
      )
    );
    const hiddenCount = items.length - visibleCount;

    const baseText = items.slice(0, visibleCount).join(SEPARATOR);
    const suffix = hiddenCount > 0 ? ` (+${hiddenCount})` : '';

    probe.textContent = baseText + suffix;
    if (probe.scrollHeight <= twoLinesH) {
      return { visibleCount, displayText: baseText + suffix };
    }

    const ellipsisSuffix = hiddenCount > 0 ? `... (+${hiddenCount})` : '...';
    let lo = 0;
    let hi = baseText.length;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      probe.textContent = baseText.slice(0, mid) + ellipsisSuffix;
      if (probe.scrollHeight <= twoLinesH) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }

    return {
      visibleCount,
      displayText: (lo > 0 ? baseText.slice(0, lo) : '') + ellipsisSuffix,
    };
  } finally {
    probe.remove();
  }
}
